import LeftChevronIcon from '@/CustomIcons/LeftChevron'
import { Button } from '@/UIComponents2/Button'
import { PortalContext } from '@/UIComponents2/PortalContext'
import { DropdownMenu as RadMenu } from 'radix-ui'
import { forwardRef, useContext, useMemo, useState } from 'react'
import { sessionViewModeState, useViewMode } from '../shared/useViewMode'
import CurvedArrowRightIcon from '@/CustomIcons/CurvedArrowRightIcon'
import { useCollapsibleGroup } from '@/UIComponents2/useCollapsibleGroup'
import { env } from '@/services/EnvironmentVariables'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utlis/cn'
import CameraIcon from '@/CustomIcons/CameraIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import MixerIcon from '@/CustomIcons/MixerIcon'
import '@/components/dropdown-animation.css'

export function LayoutMenu() {
    const { t } = useTranslation()
    const portalContainer = useContext(PortalContext)
    const viewMode = useViewMode()
    const mixers = useCollapsibleGroup('sidebar-user-controls')
    const [isOpen, setIsOpen] = useState(false)

    const currentIcon = useMemo(() => {
        const showVideos = viewMode.showVideosInSidebar && !viewMode.sidebarVideosHidden
        if (!viewMode.galleryHidden && !viewMode.sidebarPoppedOut) {
            return <GridIcon />
        } else {
            if (!showVideos && mixers.allClosed) {
                return <ExtraCompactIcon />
            }
            if (showVideos && !mixers.allClosed) {
                return <StandardIcon />
            }
            return <CompactIcon />
        }
    }, [
        viewMode.galleryHidden,
        viewMode.sidebarVideosHidden,
        mixers.allClosed,
        viewMode.sidebarPoppedOut,
        viewMode.showVideosInSidebar,
    ])

    const renderCollapseSidebarButton = env.IS_ELECTRON || !viewMode.galleryHidden

    return (
        <RadMenu.Root onOpenChange={(open) => setIsOpen(open)} modal={false}>
            <RadMenu.Trigger asChild>
                <Button variant="tertiary" className="w-auto p-[12px] gap-[10px] py-0">
                    <div className="flex gap-[4px] items-center t-sm">
                        {currentIcon}
                        {t('View')}
                    </div>
                    <div className={cn('transition-transform duration-150', isOpen ? 'rotate-90' : '-rotate-90')}>
                        <LeftChevronIcon className="opacity-65" style={{ fontSize: '1.3rem' }} />
                    </div>
                </Button>
            </RadMenu.Trigger>

            <RadMenu.Portal container={portalContainer}>
                <RadMenu.Content
                    className="dropdown-menu-content z-30 rounded-[14px] shadow-glow py-3 px-2 gap-[5px] flex flex-col bg-muse-menu border-muse-toplit-low min-w-[230px]"
                    sideOffset={5}
                    collisionPadding={8}
                >
                    <ToggleAllInputsButton />
                    <ToggleSidebarCamerasButton />
                    {renderCollapseSidebarButton && (
                        <RadMenu.Item asChild>
                            <CollapseSidebarButton />
                        </RadMenu.Item>
                    )}
                </RadMenu.Content>
            </RadMenu.Portal>
        </RadMenu.Root>
    )
}

const ToggleAllInputsButton = forwardRef<HTMLButtonElement>((_, ref) => {
    const { t } = useTranslation()
    const collapsibleGroup = useCollapsibleGroup('sidebar-user-controls')

    return (
        <Button
            ref={ref}
            variant="rectangle"
            className="justify-between pr-2"
            onClick={() => {
                if (collapsibleGroup.allClosed) {
                    collapsibleGroup.openAll()
                } else {
                    collapsibleGroup.closeAll()
                }
            }}
        >
            <div className="flex items-center gap-2">
                <MixerIcon style={{ fontSize: '1.3rem' }} /> {t('Show Inputs')}
            </div>
            <Checkbox checked={!collapsibleGroup.allClosed} />
        </Button>
    )
})

const ToggleSidebarCamerasButton = forwardRef<HTMLButtonElement>((_, ref) => {
    const { t } = useTranslation()
    const viewMode = useViewMode()

    if (!viewMode.showVideosInSidebar) {
        return null
    }

    return (
        <Button
            ref={ref}
            variant="rectangle"
            className="justify-between pr-2"
            onClick={() => {
                sessionViewModeState.setSidebarVideosHidden(!sessionViewModeState.mode.sidebarVideosHidden)
            }}
        >
            <div className="flex items-center gap-2">
                <CameraIcon style={{ fontSize: '1.3rem' }} /> {t('Show Videos')}
            </div>
            <Checkbox checked={!viewMode.sidebarVideosHidden} />
        </Button>
    )
})

const CollapseSidebarButton = forwardRef<HTMLButtonElement>((_, ref) => {
    const { t } = useTranslation()

    return (
        <Button
            variant="rectangle"
            ref={ref}
            className="px-2 flex justify-between"
            onClick={() => {
                sessionViewModeState.setSidebarCollapsed(true)
            }}
        >
            <div>{t('Hide Sidebar')}</div> <CurvedArrowRightIcon style={{ fontSize: '1.3rem' }} />
        </Button>
    )
})

function GridIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 26" fill="none">
            <path
                d="M4.02641 5.09333C4.12378 4.26667 4.60534 4 6.00005 4C8 4 8 4.58462 8 6C8 7.41538 8 8 6.00005 8C4.60534 8 4.12378 7.73333 4.02641 6.90667C4.01062 6.66256 4 6.24205 4 6L4.02641 5.09333Z"
                fill="url(#paint0_radial)"
            />
            <path
                d="M6 4.5C6.48274 4.5 6.8063 4.53605 7.02441 4.5957C7.23363 4.65293 7.31398 4.72304 7.35449 4.77539C7.40098 4.8355 7.4465 4.93898 7.47266 5.15137C7.49935 5.36824 7.5 5.63605 7.5 6C7.5 6.36395 7.49935 6.63176 7.47266 6.84863C7.4465 7.06102 7.40098 7.1645 7.35449 7.22461C7.31398 7.27696 7.23363 7.34707 7.02441 7.4043C6.8063 7.46395 6.48274 7.5 6 7.5C5.30727 7.5 4.95355 7.42864 4.76855 7.32617C4.64775 7.25918 4.5616 7.16077 4.52441 6.85645C4.5104 6.62796 4.50041 6.245 4.5 6.01465L4.52539 5.13672C4.56304 4.83777 4.64866 4.74031 4.76855 4.67383C4.95355 4.57136 5.30727 4.5 6 4.5Z"
                stroke="url(#paint1_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M4.02641 11.0933C4.12378 10.2667 4.60534 10 6.00005 10C8 10 8 10.5846 8 12C8 13.4154 8 14 6.00005 14C4.60534 14 4.12378 13.7333 4.02641 12.9067C4.01062 12.6626 4 12.2421 4 12L4.02641 11.0933Z"
                fill="url(#paint2_radial)"
            />
            <path
                d="M6 10.5C6.48274 10.5 6.8063 10.536 7.02441 10.5957C7.23363 10.6529 7.31398 10.723 7.35449 10.7754C7.40098 10.8355 7.4465 10.939 7.47266 11.1514C7.49935 11.3682 7.5 11.6361 7.5 12C7.5 12.3639 7.49935 12.6318 7.47266 12.8486C7.4465 13.061 7.40098 13.1645 7.35449 13.2246C7.31398 13.277 7.23363 13.3471 7.02441 13.4043C6.8063 13.464 6.48274 13.5 6 13.5C5.30727 13.5 4.95355 13.4286 4.76855 13.3262C4.64775 13.2592 4.5616 13.1608 4.52441 12.8564C4.5104 12.628 4.50041 12.245 4.5 12.0146L4.52539 11.1367C4.56304 10.8378 4.64866 10.7403 4.76855 10.6738C4.95355 10.5714 5.30727 10.5 6 10.5Z"
                stroke="url(#paint3_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M4.02641 17.0933C4.12378 16.2667 4.60534 16 6.00005 16C8 16 8 16.5846 8 18C8 19.4154 8 20 6.00005 20C4.60534 20 4.12378 19.7333 4.02641 18.9067C4.01062 18.6626 4 18.2421 4 18L4.02641 17.0933Z"
                fill="url(#paint4_radial)"
            />
            <path
                d="M6 16.5C6.48274 16.5 6.8063 16.536 7.02441 16.5957C7.23363 16.6529 7.31398 16.723 7.35449 16.7754C7.40098 16.8355 7.4465 16.939 7.47266 17.1514C7.49935 17.3682 7.5 17.6361 7.5 18C7.5 18.3639 7.49935 18.6318 7.47266 18.8486C7.4465 19.061 7.40098 19.1645 7.35449 19.2246C7.31398 19.277 7.23363 19.3471 7.02441 19.4043C6.8063 19.464 6.48274 19.5 6 19.5C5.30727 19.5 4.95355 19.4286 4.76855 19.3262C4.64775 19.2592 4.5616 19.1608 4.52441 18.8564C4.5104 18.628 4.50041 18.245 4.5 18.0146L4.52539 17.1367C4.56304 16.8378 4.64866 16.7403 4.76855 16.6738C4.95355 16.5714 5.30727 16.5 6 16.5Z"
                stroke="url(#paint5_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M10.0264 5.09333C10.1238 4.26667 10.6053 4 12 4C14 4 14 4.58462 14 6C14 7.41538 14 8 12 8C10.6053 8 10.1238 7.73333 10.0264 6.90667C10.0106 6.66256 10 6.24205 10 6L10.0264 5.09333Z"
                fill="url(#paint6_radial)"
            />
            <path
                d="M12 4.5C12.4827 4.5 12.8063 4.53605 13.0244 4.5957C13.2336 4.65293 13.314 4.72304 13.3545 4.77539C13.401 4.8355 13.4465 4.93898 13.4727 5.15137C13.4994 5.36824 13.5 5.63605 13.5 6C13.5 6.36395 13.4994 6.63176 13.4727 6.84863C13.4465 7.06102 13.401 7.1645 13.3545 7.22461C13.314 7.27696 13.2336 7.34707 13.0244 7.4043C12.8063 7.46395 12.4827 7.5 12 7.5C11.3073 7.5 10.9535 7.42864 10.7686 7.32617C10.6477 7.25918 10.5616 7.16077 10.5244 6.85645C10.5104 6.62796 10.5004 6.245 10.5 6.01465L10.5254 5.13672C10.563 4.83777 10.6487 4.74031 10.7686 4.67383C10.9535 4.57136 11.3073 4.5 12 4.5Z"
                stroke="url(#paint7_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M10.0264 11.0933C10.1238 10.2667 10.6053 10 12 10C14 10 14 10.5846 14 12C14 13.4154 14 14 12 14C10.6053 14 10.1238 13.7333 10.0264 12.9067C10.0106 12.6626 10 12.2421 10 12L10.0264 11.0933Z"
                fill="url(#paint8_radial)"
            />
            <path
                d="M12 10.5C12.4827 10.5 12.8063 10.536 13.0244 10.5957C13.2336 10.6529 13.314 10.723 13.3545 10.7754C13.401 10.8355 13.4465 10.939 13.4727 11.1514C13.4994 11.3682 13.5 11.6361 13.5 12C13.5 12.3639 13.4994 12.6318 13.4727 12.8486C13.4465 13.061 13.401 13.1645 13.3545 13.2246C13.314 13.277 13.2336 13.3471 13.0244 13.4043C12.8063 13.464 12.4827 13.5 12 13.5C11.3073 13.5 10.9535 13.4286 10.7686 13.3262C10.6477 13.2592 10.5616 13.1608 10.5244 12.8564C10.5104 12.628 10.5004 12.245 10.5 12.0146L10.5254 11.1367C10.563 10.8378 10.6487 10.7403 10.7686 10.6738C10.9535 10.5714 11.3073 10.5 12 10.5Z"
                stroke="url(#paint9_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M10.0264 17.0933C10.1238 16.2667 10.6053 16 12 16C14 16 14 16.5846 14 18C14 19.4154 14 20 12 20C10.6053 20 10.1238 19.7333 10.0264 18.9067C10.0106 18.6626 10 18.2421 10 18L10.0264 17.0933Z"
                fill="url(#paint10_radial)"
            />
            <path
                d="M12 16.5C12.4827 16.5 12.8063 16.536 13.0244 16.5957C13.2336 16.6529 13.314 16.723 13.3545 16.7754C13.401 16.8355 13.4465 16.939 13.4727 17.1514C13.4994 17.3682 13.5 17.6361 13.5 18C13.5 18.3639 13.4994 18.6318 13.4727 18.8486C13.4465 19.061 13.401 19.1645 13.3545 19.2246C13.314 19.277 13.2336 19.3471 13.0244 19.4043C12.8063 19.464 12.4827 19.5 12 19.5C11.3073 19.5 10.9535 19.4286 10.7686 19.3262C10.6477 19.2592 10.5616 19.1608 10.5244 18.8564C10.5104 18.628 10.5004 18.245 10.5 18.0146L10.5254 17.1367C10.563 16.8378 10.6487 16.7403 10.7686 16.6738C10.9535 16.5714 11.3073 16.5 12 16.5Z"
                stroke="url(#paint11_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M16.0264 5.09333C16.1238 4.26667 16.6053 4 18 4C20 4 20 4.58462 20 6C20 7.41538 20 8 18 8C16.6053 8 16.1238 7.73333 16.0264 6.90667C16.0106 6.66256 16 6.24205 16 6L16.0264 5.09333Z"
                fill="url(#paint12_radial)"
            />
            <path
                d="M18 4.5C18.4827 4.5 18.8063 4.53605 19.0244 4.5957C19.2336 4.65293 19.314 4.72304 19.3545 4.77539C19.401 4.8355 19.4465 4.93898 19.4727 5.15137C19.4994 5.36824 19.5 5.63605 19.5 6C19.5 6.36395 19.4994 6.63176 19.4727 6.84863C19.4465 7.06102 19.401 7.1645 19.3545 7.22461C19.314 7.27696 19.2336 7.34707 19.0244 7.4043C18.8063 7.46395 18.4827 7.5 18 7.5C17.3073 7.5 16.9535 7.42864 16.7686 7.32617C16.6477 7.25918 16.5616 7.16077 16.5244 6.85645C16.5104 6.62796 16.5004 6.245 16.5 6.01465L16.5254 5.13672C16.563 4.83777 16.6487 4.74031 16.7686 4.67383C16.9535 4.57136 17.3073 4.5 18 4.5Z"
                stroke="url(#paint13_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M16.0264 11.0933C16.1238 10.2667 16.6053 10 18 10C20 10 20 10.5846 20 12C20 13.4154 20 14 18 14C16.6053 14 16.1238 13.7333 16.0264 12.9067C16.0106 12.6626 16 12.2421 16 12L16.0264 11.0933Z"
                fill="url(#paint14_radial)"
            />
            <path
                d="M18 10.5C18.4827 10.5 18.8063 10.536 19.0244 10.5957C19.2336 10.6529 19.314 10.723 19.3545 10.7754C19.401 10.8355 19.4465 10.939 19.4727 11.1514C19.4994 11.3682 19.5 11.6361 19.5 12C19.5 12.3639 19.4994 12.6318 19.4727 12.8486C19.4465 13.061 19.401 13.1645 19.3545 13.2246C19.314 13.277 19.2336 13.3471 19.0244 13.4043C18.8063 13.464 18.4827 13.5 18 13.5C17.3073 13.5 16.9535 13.4286 16.7686 13.3262C16.6477 13.2592 16.5616 13.1608 16.5244 12.8564C16.5104 12.628 16.5004 12.245 16.5 12.0146L16.5254 11.1367C16.563 10.8378 16.6487 10.7403 16.7686 10.6738C16.9535 10.5714 17.3073 10.5 18 10.5Z"
                stroke="url(#paint15_linear)"
                strokeOpacity="0.2"
            />
            <path
                d="M16.0264 17.0933C16.1238 16.2667 16.6053 16 18 16C20 16 20 16.5846 20 18C20 19.4154 20 20 18 20C16.6053 20 16.1238 19.7333 16.0264 18.9067C16.0106 18.6626 16 18.2421 16 18L16.0264 17.0933Z"
                fill="url(#paint16_radial)"
            />
            <path
                d="M18 16.5C18.4827 16.5 18.8063 16.536 19.0244 16.5957C19.2336 16.6529 19.314 16.723 19.3545 16.7754C19.401 16.8355 19.4465 16.939 19.4727 17.1514C19.4994 17.3682 19.5 17.6361 19.5 18C19.5 18.3639 19.4994 18.6318 19.4727 18.8486C19.4465 19.061 19.401 19.1645 19.3545 19.2246C19.314 19.277 19.2336 19.3471 19.0244 19.4043C18.8063 19.464 18.4827 19.5 18 19.5C17.3073 19.5 16.9535 19.4286 16.7686 19.3262C16.6477 19.2592 16.5616 19.1608 16.5244 18.8564C16.5104 18.628 16.5004 18.245 16.5 18.0146L16.5254 17.1367C16.563 16.8378 16.6487 16.7403 16.7686 16.6738C16.9535 16.5714 17.3073 16.5 18 16.5Z"
                stroke="url(#paint17_linear)"
                strokeOpacity="0.2"
            />
            <defs>
                <radialGradient
                    id="paint0_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(4.01695 4.38461) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint1_linear" x1="6" y1="4" x2="6" y2="8" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint2_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(4.01695 10.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint3_linear" x1="6" y1="10" x2="6" y2="14" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint4_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(4.01695 16.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint5_linear" x1="6" y1="16" x2="6" y2="20" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint6_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(10.0169 4.38461) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint7_linear" x1="12" y1="4" x2="12" y2="8" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint8_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(10.0169 10.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint9_linear" x1="12" y1="10" x2="12" y2="14" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint10_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(10.0169 16.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint11_linear" x1="12" y1="16" x2="12" y2="20" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint12_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(16.0169 4.38461) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint13_linear" x1="18" y1="4" x2="18" y2="8" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint14_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(16.0169 10.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint15_linear" x1="18" y1="10" x2="18" y2="14" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint16_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(16.0169 16.3846) rotate(47.375) scale(5.9067 17.1466)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint17_linear" x1="18" y1="16" x2="18" y2="20" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}

function ExtraCompactIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" height="2" viewBox="0 0 24 2" fill="none">
            <g transform="translate(5 0)">
                <path
                    d="M0.0990411 0.546667C0.464164 0.133333 2.27004 0 7.50018 0C15 0 15 0.292308 15 1C15 1.70769 15 2 7.50018 2C2.27004 2 0.464164 1.86667 0.0990411 1.45333C0.039832 1.33128 0 1.12103 0 1L0.0990411 0.546667Z"
                    fill="url(#paint0_radial)"
                />
                <path
                    d="M7.5 0.5C11.2634 0.5 13.0911 0.574802 13.9775 0.727539C14.317 0.786037 14.4502 0.84844 14.4951 0.874023C14.4967 0.896667 14.5 0.93591 14.5 1C14.5 1.06318 14.4967 1.10221 14.4951 1.125C14.4506 1.15044 14.3181 1.21378 13.9775 1.27246C13.0911 1.4252 11.2634 1.5 7.5 1.5C4.88429 1.5 3.14196 1.46629 2.01172 1.38281C1.44553 1.34099 1.05259 1.2883 0.788086 1.22656C0.649861 1.19428 0.567584 1.16291 0.521484 1.14258C0.520078 1.13647 0.517935 1.13039 0.516602 1.12402C0.508874 1.0871 0.50625 1.05504 0.503906 1.03223L0.544922 0.847656C0.593722 0.828009 0.670735 0.800847 0.788086 0.773438C1.05259 0.711695 1.44553 0.659015 2.01172 0.617188C3.14196 0.533711 4.88429 0.500002 7.5 0.5Z"
                    stroke="url(#paint1_linear)"
                    strokeOpacity="0.2"
                />
            </g>
            <defs>
                <radialGradient
                    id="paint0_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(0.06356 0.192307) rotate(8.24319) scale(15.1566 12.5292)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint1_linear" x1="7.5" y1="0" x2="7.5" y2="2" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}

function CompactIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="19" viewBox="0 0 20 20" fill="none">
            <foreignObject x="-12.5573" y="-7.55735" width="44.1147" height="35.1147">
                <div
                    style={{
                        backdropFilter: 'blur(7.28px)',
                        clipPath: 'url(#bgblur_0_1093_25849_clip_path)',
                        height: '100%',
                        width: '100%',
                    }}
                ></div>
            </foreignObject>
            <g>
                <path
                    d="M2.09904 8.64C2.46416 7.4 4.27004 7 9.50018 7C17 7 17 7.87692 17 10C17 12.1231 17 13 9.50018 13C4.27004 13 2.46416 12.6 2.09904 11.36C2.03983 10.9938 2 10.3631 2 10L2.09904 8.64Z"
                    fill="url(#paint0_radial_1093_25849)"
                />
                <path
                    d="M9.5 7.5C11.3682 7.5 12.7534 7.55457 13.7812 7.66699C14.8176 7.78038 15.4473 7.94906 15.833 8.14844C16.1877 8.33183 16.3294 8.53583 16.4053 8.78223C16.4957 9.07615 16.5 9.44578 16.5 10C16.5 10.5542 16.4957 10.9239 16.4053 11.2178C16.3294 11.4642 16.1877 11.6682 15.833 11.8516C15.4473 12.0509 14.8176 12.2196 13.7812 12.333C12.7534 12.4454 11.3682 12.5 9.5 12.5C6.88348 12.5 5.17432 12.399 4.08301 12.1572C3.01586 11.9207 2.69886 11.5869 2.58789 11.2412C2.538 10.9073 2.50116 10.3497 2.5 10.0166L2.5918 8.74512C2.70677 8.40424 3.02978 8.07618 4.08301 7.84277C5.17432 7.60097 6.88348 7.5 9.5 7.5Z"
                    stroke="url(#paint1_linear_1093_25849)"
                    strokeOpacity="0.2"
                />
            </g>
            <defs>
                <clipPath id="bgblur_0_1093_25849_clip_path" transform="translate(12.5573 7.55735)">
                    <path d="M2.09904 8.64C2.46416 7.4 4.27004 7 9.50018 7C17 7 17 7.87692 17 10C17 12.1231 17 13 9.50018 13C4.27004 13 2.46416 12.6 2.09904 11.36C2.03983 10.9938 2 10.3631 2 10L2.09904 8.64Z" />
                </clipPath>
                <radialGradient
                    id="paint0_radial_1093_25849"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(2.06356 7.57692) rotate(23.4905) scale(16.3554 34.8323)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient
                    id="paint1_linear_1093_25849"
                    x1="9.5"
                    y1="7"
                    x2="9.5"
                    y2="13"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}

function StandardIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <g>
                <path
                    d="M2.09904 4.64C2.46416 3.4 4.27004 3 9.50018 3C17 3 17 3.87692 17 6C17 8.12308 17 9 9.50018 9C4.27004 9 2.46416 8.6 2.09904 7.36C2.03983 6.99385 2 6.36308 2 6L2.09904 4.64Z"
                    fill="url(#paint0_radial)"
                />
                <path
                    d="M9.5 3.5C11.3682 3.5 12.7534 3.55457 13.7812 3.66699C14.8176 3.78038 15.4473 3.94906 15.833 4.14844C16.1877 4.33183 16.3294 4.53583 16.4053 4.78223C16.4957 5.07615 16.5 5.44578 16.5 6C16.5 6.55422 16.4957 6.92385 16.4053 7.21777C16.3294 7.46417 16.1877 7.66817 15.833 7.85156C15.4473 8.05094 14.8176 8.21962 13.7812 8.33301C12.7534 8.44543 11.3682 8.5 9.5 8.5C6.88348 8.5 5.17432 8.39903 4.08301 8.15723C3.01586 7.92073 2.69886 7.58694 2.58789 7.24121C2.538 6.90726 2.50116 6.34969 2.5 6.0166L2.5918 4.74512C2.70677 4.40424 3.02978 4.07618 4.08301 3.84277C5.17432 3.60097 6.88348 3.5 9.5 3.5Z"
                    stroke="url(#paint1_linear)"
                    strokeOpacity="0.2"
                />
            </g>
            <g>
                <path
                    d="M2.09904 12.64C2.46416 11.4 4.27004 11 9.50018 11C17 11 17 11.8769 17 14C17 16.1231 17 17 9.50018 17C4.27004 17 2.46416 16.6 2.09904 15.36C2.03983 14.9938 2 14.3631 2 14L2.09904 12.64Z"
                    fill="url(#paint2_radial)"
                />
                <path
                    d="M9.5 11.5C11.3682 11.5 12.7534 11.5546 13.7812 11.667C14.8176 11.7804 15.4473 11.9491 15.833 12.1484C16.1877 12.3318 16.3294 12.5358 16.4053 12.7822C16.4957 13.0761 16.5 13.4458 16.5 14C16.5 14.5542 16.4957 14.9239 16.4053 15.2178C16.3294 15.4642 16.1877 15.6682 15.833 15.8516C15.4473 16.0509 14.8176 16.2196 13.7812 16.333C12.7534 16.4454 11.3682 16.5 9.5 16.5C6.88348 16.5 5.17432 16.399 4.08301 16.1572C3.01586 15.9207 2.69886 15.5869 2.58789 15.2412C2.538 14.9073 2.50116 14.3497 2.5 14.0166L2.5918 12.7451C2.70677 12.4042 3.02978 12.0762 4.08301 11.8428C5.17432 11.601 6.88348 11.5 9.5 11.5Z"
                    stroke="url(#paint3_linear)"
                    strokeOpacity="0.2"
                />
            </g>
            <defs>
                <radialGradient
                    id="paint0_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(2.06356 3.57692) rotate(23.4905) scale(16.3554 34.8323)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint1_linear" x1="9.5" y1="3" x2="9.5" y2="9" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
                <radialGradient
                    id="paint2_radial"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(2.06356 11.5769) rotate(23.4905) scale(16.3554 34.8323)"
                >
                    <stop stopColor="#FCE640" />
                    <stop offset="0.770739" stopColor="#F49629" />
                    <stop offset="1" stopColor="#F17E22" />
                </radialGradient>
                <linearGradient id="paint3_linear" x1="9.5" y1="11" x2="9.5" y2="17" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
            </defs>
        </svg>
    )
}
